import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type Connectivity, type EnrichmentResult } from '../lib/api.ts';

/**
 * Connectivity state, and the backfill that follows it.
 *
 * A Wi-Fi OBD adapter is its own access point with no route to the internet, so
 * being offline mid-scan is the normal working condition rather than an error.
 * The scan is unaffected by it. What has to wait is the two lookups that need a
 * network, and the honest thing is to name them while they are outstanding
 * instead of showing blanks and letting someone assume the vehicle is at fault.
 *
 * When connectivity comes back the backfill runs on its own. Making the user
 * remember to press a button is how records stay half-filled forever.
 */
export function ConnectivityBanner({ onEnriched }: { onEnriched: () => void }) {
  const [state, setState] = useState<Connectivity | null>(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<EnrichmentResult | null>(null);
  const [dismissed, setDismissed] = useState(false);

  // Remembering the previous reading is what makes "came back online" a
  // detectable event rather than just a state.
  const wasOffline = useRef(false);

  const enrich = useCallback(
    async (auto: boolean) => {
      setRunning(true);
      try {
        const res = await api.runEnrichment();
        setResult(res);
        setDismissed(false);
        if (res.decoded.length > 0) onEnriched();
        setState(await api.connectivity());
      } catch {
        // Offline again already, or NHTSA is down. The banner keeps saying so.
        if (!auto) setState(await api.connectivity(true).catch(() => null));
      } finally {
        setRunning(false);
      }
    },
    [onEnriched],
  );

  useEffect(() => {
    let stopped = false;

    const poll = async () => {
      try {
        const next = await api.connectivity();
        if (stopped) return;
        setState(next);

        if (wasOffline.current && next.online && next.pending.length > 0) {
          void enrich(true);
        }
        wasOffline.current = !next.online;
      } catch {
        // The bridge itself is unreachable; App already reports that.
      }
    };

    void poll();
    const id = setInterval(poll, 15_000);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, [enrich]);

  if (!state) return null;

  const pendingFields = [...new Set(state.pending.flatMap((p) => p.missing))];

  // Offline with outstanding lookups: say what is waiting and why.
  if (!state.online) {
    return (
      <div className="banner warn" style={{ marginBottom: 16 }}>
        <strong>No internet route.</strong> Expected while joined to the adapter's Wi-Fi, which has no uplink. Scanning
        is unaffected: the VIN, every code, readiness, freeze frame, on-board tests and calibration all come from the
        vehicle and are being stored in full.
        {pendingFields.length > 0 ? (
          <>
            {' '}
            Waiting on a connection:{' '}
            <strong>{pendingFields.join(', ')}</strong> for {state.pending.length} vehicle
            {state.pending.length === 1 ? '' : 's'}. These fill in on their own once you rejoin a normal network.
          </>
        ) : (
          ' Nothing is currently waiting on a lookup.'
        )}
      </div>
    );
  }

  // Back online with work outstanding, and the auto-run has not finished yet.
  if (state.pending.length > 0) {
    return (
      <div className="banner info" style={{ marginBottom: 16 }}>
        <strong>{running ? 'Filling in deferred data…' : 'Deferred lookups outstanding.'}</strong>{' '}
        {state.pending.length} vehicle{state.pending.length === 1 ? '' : 's'} still missing{' '}
        {pendingFields.join(', ')}.{' '}
        {!running && (
          <button className="tiny" onClick={() => enrich(false)} style={{ marginLeft: 4 }}>
            Fill in now
          </button>
        )}
      </div>
    );
  }

  // Report what the backfill actually changed, once.
  if (result && !dismissed && (result.decoded.length > 0 || result.failed.length > 0)) {
    return (
      <div className={`banner ${result.failed.length ? 'warn' : 'ok'}`} style={{ marginBottom: 16 }}>
        <strong>Deferred data filled in.</strong>{' '}
        {result.decoded.map((d) => d.summary).join('; ')}
        {result.recallsChecked > 0 && ` · recalls checked for ${result.recallsChecked} vehicle(s)`}
        {result.failed.length > 0 && ` · ${result.failed.length} still incomplete`}
        <button className="tiny ghost" onClick={() => setDismissed(true)} style={{ marginLeft: 8 }}>
          dismiss
        </button>
      </div>
    );
  }

  return null;
}
